<script>
// Painel flutuante com os subitens de um módulo da sidebar. Apenas apresentacional:
// recebe os subitens e a posição (top/left do item âncora) e emite `select`.
// Vive em `position: fixed` (fora do overflow da sidebar), ancorado às coordenadas
// do item via getBoundingClientRect calculado no pai. Re-emite mouseenter/mouseleave
// para o pai manter o painel aberto enquanto o mouse percorre o corredor item→painel.
import { activeSubitemKey } from '@modules/navegacao-v2/utils/active-subitem'
import { isActiveRoute } from '@modules/navegacao-v2/utils/visible-modules'

// Margem mínima até a borda da viewport e altura mínima usada para decidir se o
// painel cabe abaixo do item (senão ancora pela base, crescendo para cima).
const VIEWPORT_MARGIN = 12
const MIN_PANEL_HEIGHT = 120
const DEFAULT_VIEWPORT_HEIGHT = 0
const NO_ACTIVE_KEY = ''

// Critério de destaque do item ativo. 'route' (padrão) casa por prefixo de rota e
// distingue subitens do mesmo módulo; 'module' casa pela chave de módulo e serve o
// painel de Parte no modo recolhido, onde os itens são módulos inteiros.
export const MATCH_BY = { ROUTE: 'route', MODULE: 'module' }

export default {
  name: 'NavFlyout',
  props: {
    title: { type: String, default: '' },
    items: { type: Array, default: () => [] },
    anchorTop: { type: Number, default: 0 },
    anchorLeft: { type: Number, default: 0 },
    activeRouteName: { type: String, default: '' },
    matchBy: {
      type: String,
      default: MATCH_BY.ROUTE,
      validator: (v) => [MATCH_BY.ROUTE, MATCH_BY.MODULE].includes(v),
    },
  },
  computed: {
    activeModuleItemKey() {
      const match = (this.items || []).find(
        (item) => item && isActiveRoute(item.routeName, this.activeRouteName)
      )
      return match ? match.key : NO_ACTIVE_KEY
    },
    activeKey() {
      if (this.matchBy === MATCH_BY.MODULE) return this.activeModuleItemKey
      return activeSubitemKey(this.items, this.activeRouteName)
    },
    viewportHeight() {
      if (typeof window === 'undefined') return DEFAULT_VIEWPORT_HEIGHT
      return window.innerHeight || DEFAULT_VIEWPORT_HEIGHT
    },
    // Cabe abaixo do item se houver espaço para a altura mínima até a margem.
    fitsBelow() {
      const limit = this.viewportHeight - VIEWPORT_MARGIN
      return this.anchorTop + MIN_PANEL_HEIGHT <= limit
    },
    panelStyle() {
      const left = `${this.anchorLeft}px`
      if (this.fitsBelow) {
        return {
          left,
          top: `${this.anchorTop}px`,
          maxHeight: `${this.viewportHeight - this.anchorTop - VIEWPORT_MARGIN}px`,
        }
      }
      return {
        left,
        bottom: `${VIEWPORT_MARGIN}px`,
        maxHeight: `${this.anchorTop}px`,
      }
    },
  },
  methods: {
    isActive(item) {
      return item.key === this.activeKey
    },
  },
}
</script>

<template>
  <div
    class="nav-flyout"
    :style="panelStyle"
    @mouseenter="$emit('mouseenter')"
    @mouseleave="$emit('mouseleave')"
  >
    <div class="nav-flyout__title">{{ title }}</div>
    <button
      v-for="item in items"
      :key="item.key"
      class="nav-flyout__item"
      :class="{ 'nav-flyout__item--active': isActive(item) }"
      type="button"
      @click="$emit('select', item)"
    >
      {{ item.label }}
    </button>
  </div>
</template>

<style lang="sass" scoped>
.nav-flyout
  position: fixed
  z-index: 80
  min-width: 220px
  max-width: 280px
  overflow-y: auto
  padding: 8px
  border-radius: 12px
  background: var(--bg-navbar, #030309)
  border: 1px solid rgba(var(--nav-fg-rgb, 255, 255, 255), 0.08)
  box-shadow: 0 18px 48px rgba(3, 3, 9, 0.45)
  // Reposicionamento suave ao passar o hover de um módulo para outro.
  transition: top 0.16s ease, left 0.16s ease

  &__title
    padding: 8px 12px 6px
    font-size: 10px
    font-weight: 700
    letter-spacing: 0.14em
    text-transform: uppercase
    color: rgba(var(--nav-fg-rgb, 255, 255, 255), 0.45)

  &__item
    display: block
    width: 100%
    box-sizing: border-box
    padding: 9px 12px
    border: 0
    border-radius: 9px
    background: transparent
    color: rgba(var(--nav-fg-rgb, 255, 255, 255), 0.7)
    font-family: var(--font-body, inherit)
    font-size: 13px
    font-weight: 600
    text-align: left
    white-space: nowrap
    overflow: hidden
    text-overflow: ellipsis
    cursor: pointer
    transition: background 0.15s, color 0.15s

    &:hover
      background: rgba(var(--nav-fg-rgb, 255, 255, 255), 0.06)
      color: var(--nav-fg, #fff)

    &--active
      color: var(--nav-fg, #fff)

      &::before
        content: ''
        position: absolute
        left: 0
        width: 3px
        height: 18px
        margin-top: 1px
        border-radius: 0 3px 3px 0
        background: var(--primary)

      background: rgba(var(--nav-fg-rgb, 255, 255, 255), 0.06)

// Abertura/fechamento (transition name="flyout"): fade + leve deslize lateral.
// Abertura um pouco mais lenta que o fechamento, com easing de saída.
.flyout-enter-active
  transition: opacity 0.17s ease-out, transform 0.17s ease-out

.flyout-leave-active
  transition: opacity 0.12s ease-in, transform 0.12s ease-in

.flyout-enter,
.flyout-leave-to
  opacity: 0
  transform: translateX(-8px)
</style>
